import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useNotification } from "@hooks/useNotification";
import { useQuery } from "@hooks/useQuery";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { decodeHTML, encodeHTML } from "@utils/CommonUtils";
import { Tabs } from "antd";
import { useEffect } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { useHasAccess } from "~/Hooks/useHasAccess";
import { post, put } from "~/Services";
import { magpHost } from "~/Utils";
import { fields } from "./constants";
import {  useMobileLoginPage } from "./Login.hook";
import { MobileLoginPage } from "./MobileLoginPage/MobileLoginPage";
import { WebLoginPage } from "./WebLoginPage/WebLoginPage";

export const LoginMAGP = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const { openToast } = useNotification();
  const { hasRole } = useHasAccess();

  const { data: loginPageData, error, isLoading, isError, refetch: refetchData } = useMobileLoginPage();
  const {
    data: loginPageById,
    isLoading: isLoadingLoginPage,
    refetch: refetchDataById,
  } = useDocumentDetails(query?.get("id") || "");

  useEffect(() => {
    refetchData();
  }, []);

  useEffect(() => {
    if (query?.get("id")) {
      refetchDataById();
    }
  }, [query?.get("id")]);

  useEffect(() => {
    const activeTab = query.get("activeTab");
    const subActiveTab = query.get("subActiveTab");

    if (!activeTab) {
      navigate(`/dedicated/magp/login?activeTab=mobileLoginPage`, {
        replace: true,
      });
    } else if (activeTab === "webLoginPage" && !subActiveTab) {
      navigate(`/dedicated/magp/login?activeTab=webLoginPage&subActiveTab=mainLoginPage`, {
        replace: true,
      });
    }
  }, [query, navigate]);

  const loginPageConfig: FormConfig = {
    initialValues: {
      title: loginPageById?.payload ? loginPageById?.payload?.title : loginPageData?.[0]?.title,
      field_welcome_page_image: loginPageById?.payload
        ? decodeHTML([
            {
              uid: "-1",
              name: "MAGP_image.png",
              status: "done",
              url: loginPageById?.payload?.field_welcome_page_image,
            },
          ])
        : decodeHTML([
            {
              uid: "-1",
              name: "MAGP_image.png",
              status: "done",
              url: loginPageData?.[0]?.field_welcome_page_image,
            },
          ]),
      field_welcome_text: loginPageById?.payload
        ? decodeHTML(loginPageById?.payload?.field_welcome_text)
        : decodeHTML(loginPageData?.[0]?.field_welcome_text),
      field_welcome_ms: loginPageById?.payload
        ? decodeHTML(loginPageById?.payload?.field_welcome_ms)
        : decodeHTML(loginPageData?.[0]?.field_welcome_ms),
      field_welcome_zh: loginPageById?.payload
        ? decodeHTML(loginPageById?.payload?.field_welcome_zh)
        : decodeHTML(loginPageData?.[0]?.field_welcome_zh),
      field_welcome_procurement_text: loginPageById?.payload
        ? decodeHTML(loginPageById?.payload?.field_welcome_procurement_text)
        : decodeHTML(loginPageData?.[0]?.field_welcome_procurement_text),
      field_welcome_procurement_ms: loginPageById?.payload
        ? decodeHTML(loginPageById?.payload?.field_welcome_procurement_ms)
        : decodeHTML(loginPageData?.[0]?.field_welcome_procurement_ms),
      field_welcome_procurement_zh: loginPageById?.payload
        ? decodeHTML(loginPageById?.payload?.field_welcome_procurement_zh)
        : decodeHTML(loginPageData?.[0]?.field_welcome_procurement_zh),
      field_welcome_guidelines_text: loginPageById?.payload
        ? decodeHTML(loginPageById?.payload?.field_welcome_guidelines_text)
        : decodeHTML(loginPageData?.[0]?.field_welcome_guidelines_text),
      field_welcome_guidelines_ms: loginPageById?.payload
        ? decodeHTML(loginPageById?.payload?.field_welcome_guidelines_ms)
        : decodeHTML(loginPageData?.[0]?.field_welcome_guidelines_ms),
      field_welcome_guidelines_zh: loginPageById?.payload
        ? decodeHTML(loginPageById?.payload?.field_welcome_guidelines_zh)
        : decodeHTML(loginPageData?.[0]?.field_welcome_guidelines_zh),
      field_guest_login_text: loginPageById?.payload
        ? decodeHTML(loginPageById?.payload?.field_guest_login_text)
        : decodeHTML(loginPageData?.[0]?.field_guest_login_text),
      field_guest_login_text_malay: loginPageById?.payload
        ? decodeHTML(loginPageById?.payload?.field_guest_login_text_malay)
        : decodeHTML(loginPageData?.[0]?.field_guest_login_text_malay),
      field_guest_login_text_zh: loginPageById?.payload
        ? decodeHTML(loginPageById?.payload?.field_guest_login_text_zh)
        : decodeHTML(loginPageData?.[0]?.field_guest_login_text_zh),
      field_vendor_login_text: loginPageById?.payload
        ? decodeHTML(loginPageById?.payload?.field_vendor_login_text)
        : decodeHTML(loginPageData?.[0]?.field_vendor_login_text),
      field_vendor_login_text_ms: loginPageById?.payload
        ? decodeHTML(loginPageById?.payload?.field_vendor_login_text_ms)
        : decodeHTML(loginPageData?.[0]?.field_vendor_login_text_ms),
      field_vendor_login_text_zh: loginPageById?.payload
        ? decodeHTML(loginPageById?.payload?.field_vendor_login_text_zh)
        : decodeHTML(loginPageData?.[0]?.field_vendor_login_text_zh),
      field_staff_login_text: loginPageById?.payload
        ? decodeHTML(loginPageById?.payload?.field_staff_login_text)
        : decodeHTML(loginPageData?.[0]?.field_staff_login_text),
      field_staff_login_text_ms: loginPageById?.payload
        ? decodeHTML(loginPageById?.payload?.field_staff_login_text_ms)
        : decodeHTML(loginPageData?.[0]?.field_staff_login_text_ms),
      field_staff_login_text_zh: loginPageById?.payload
        ? decodeHTML(loginPageById?.payload?.field_staff_login_text_zh)
        : decodeHTML(loginPageData?.[0]?.field_staff_login_text_zh),
    },
    fields: fields.mobileLoginPage,
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let excludedKeys: any = [];
    // for (let key in data) {
    //   if (typeof data[key] === "string" && !excludedKeys.includes(key)) {
    //     data[key] = encodeHTML(data[key]);
    //   }
    // }
    let imageKeys: any = [
      "fbImage",
      "field_purchase_login_pop_image",
      "field_login_image",
      "filledImage",
      "googleImage",
      "showImage",
      "field_logout_image",
      "field_welcome_page_image",
    ];
    for (const key of imageKeys) {
      try {
        let field = data[key];
        if (field[0].originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "MAGP");
          const res: any = await post("management/upload-file", formData);

          if (res) {
            data[key] = res.response.data;
          }
        } else {
          data[key] = data[key][0].url;
        }
      } catch (error) {}
    }

    const payload = {
      action: action,
      method: method,
      module: "LOGIN",
      subModule: query?.get("activeTab"),
      type: "DEDICATED",
      application: "MAGP",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "MAGP",
          apiUrl: apiUrl,
        },
      ],
      payload: {
        ...data,
        field_camera_created_at: "2024-04-09T01:03:09",
        field_camera_updated_at: "2024-04-09T01:03:09",
        published: true,
      },
      status: status,
    };
    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response:any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === 'OK' && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else{
            navigate("/dedicated/status-information?activeTab=draft");
          }
          refetchDataById();
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response:any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === 'OK' && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else{
            navigate("/dedicated/status-information?activeTab=draft");
          }
          refetchDataById();
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  const items = [
    {
      label: "Mobile Login Page",
      key: "mobileLoginPage",
      children: (
        <DynamicForm
          isLoading={isLoading || isLoadingLoginPage}
          config={loginPageConfig}
          onSubmit={(data, buttonValue, reviewNotes) =>
            onSubmit(
              data,
              `${magpHost}/api/welcome-login-page/${loginPageData?.[0]?.id}`,
              "UPDATE",
              "PATCH",
              buttonValue == "save" ? "DRAFT" : "PENDING",
              reviewNotes
            )
          }
        />
      ),
    },
    // {
    //   label: "Mobile Calendar Page",
    //   key: "mobileCalendarPage",
    //   children: <MobileLoginPage/>
    // },
    {
      label: "Web Login Page",
      key: "webLoginPage",
      children: <WebLoginPage/>
    }
  ];

  return (
    <ContentWrapper title={"Login"}>
      <Tabs
        activeKey={query?.get("activeTab") || ""}
        items={items}
        onChange={(activeKey) => {
          navigate(`${window.location.pathname}?activeTab=${activeKey}`);
        }}
      ></Tabs>
    </ContentWrapper>
  );
};
